import * as React from 'react'
import { useCallback, useEffect } from 'react'
import type { JSX } from 'react'

import { useQuery, useLazyQuery } from '@apollo/client/react'
import { DubEmbed } from '@dub/embed-react'

import {
  Flex,
  Heading,
  Skeleton,
  Button,
  Text,
  List,
  Notice,
  Link,
  Collapsible,
  PromotionalBanner,
  WarningIcon,
  EmptyState,
  useIsDarkMode,
  toast,
} from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import { usePageVisibilityRefresh } from '~publish/hooks/usePageVisibilityRefresh'

import { AffiliateProgramRoute } from './AffiliateProgramRoute'

import style from '../Settings.module.css'
import { NewSettingsPageLayout } from '../shared/components/NewSettingsPageLayout'

import affiliateProgramStyle from './AffiliateProgram.module.css'

// See https://dub.co/docs/embed/react#options for more details
interface DubEmbedOptions {
  theme: 'light' | 'dark'
  themeOptions: {
    backgroundColor: string
  }
}

export const GetAffiliateStatus = graphql(/* GraphQL */ `
  query GetAffiliateStatus {
    affiliateData {
      status
    }
  }
`)

export const GetAffiliateEmbedData = graphql(/* GraphQL */ `
  query GetAffiliateEmbedData {
    affiliateData {
      embedData {
        token
        tokenExpiresAt
      }
    }
  }
`)

interface AffiliateStep {
  title: string
  description: string
  icon: string
  backgroundColor: string
}

interface FAQItem {
  question: string
  answer: React.ReactNode
}

const affiliateHowItWorksSteps: AffiliateStep[] = [
  {
    title: '1. Apply to join',
    description:
      'Tell us a bit about yourself. Our team personally reviews each application.',
    icon: '📋',
    backgroundColor: 'var(--global-color-purple-200)',
  },
  {
    title: '2. Share Buffer with your audience',
    description:
      'Once we approve your application, you will receive your unique link and access to your partner dashboard. Add your link to blog posts, YouTube descriptions, newsletters, social posts, or share it.',
    icon: '📣',
    backgroundColor: 'var(--global-color-yellow-200)',
  },
  {
    title: '3. Earn as your referrals grow',
    description:
      'Watch your dashboard as signups roll in, and get paid 25% commission every month for a full year for each paying customer you refer.',
    icon: '💸',
    backgroundColor: 'var(--global-color-green-200)',
  },
]

const faqItems: FAQItem[] = [
  {
    question: "How does Buffer's Partner Program work?",
    answer: (
      <>
        1. Apply to join. Tell us a bit about yourself.
        <br />
        2. We will review your application. Our team personally reviews each
        application.
        <br />
        3. Once accepted, you&apos;ll receive access to your partner space. Once
        we approve your application, you&apos;ll receive your unique link and
        access to your partner dashboard.
        <br />
        4. Share Buffer with your audience. Add your link to blog posts, YouTube
        descriptions, newsletters, social posts, or share it.
        <br />
        5. Earn as your referrals grow. Watch your dashboard as signups roll in,
        and get paid monthly.
      </>
    ),
  },
  {
    question: "When will I know if I'm approved?",
    answer:
      "We carefully review each application. If your application is approved, then you'll receive an email confirming your acceptance and the next steps on how to get started. In most cases, applications to the Buffer Partner Program are processed within 3-5 business days.",
  },
  {
    question: 'What qualifies as a referral?',
    answer:
      'A new user who signs up via your partner link and later subscribes to a paid Buffer plan qualifies as a referral.',
  },
  {
    question: 'How are referrals tracked?',
    answer:
      'Your partner link uses a short URL (join.buffer.com/…) that redirects to Buffer and adds a unique tracking ID to the destination link, allowing us to track referrals accurately. Make sure to always share your partner link to ensure proper tracking.',
  },
  {
    question: 'How does payment work?',
    answer: (
      <>
        Payments are processed monthly by{' '}
        <Link href="https://dub.co/help/article/partners-faq" external>
          Dub
        </Link>
        , who securely handles the payout process of our Partner program. Make
        sure to check their{' '}
        <Link href="https://dub.co/help/article/partners-faq" external>
          help center
        </Link>{' '}
        for any questions related to this topic.
      </>
    ),
  },
]

const AffiliateHowItWorks = (): JSX.Element => {
  return (
    <section className={style.fullWidth}>
      <Flex gap="md" direction="column" fullWidth>
        <Heading as="h2" size="small">
          How it works
        </Heading>
        <List className={affiliateProgramStyle.howItWorksList}>
          {affiliateHowItWorksSteps.map((step) => (
            <List.Item key={step.title} icon={false}>
              <Flex gap="md" align="start">
                <div
                  className={affiliateProgramStyle.stepIcon}
                  style={{ backgroundColor: step.backgroundColor }}
                >
                  <span className={affiliateProgramStyle.stepEmoji}>
                    {step.icon}
                  </span>
                </div>
                <Flex gap="xs" direction="column" style={{ flex: 1 }}>
                  <Heading as="h3" size="sm">
                    {step.title}
                  </Heading>
                  <Text color="subtle">{step.description}</Text>
                </Flex>
              </Flex>
            </List.Item>
          ))}
        </List>
      </Flex>
    </section>
  )
}

const AffiliateFAQ = (): JSX.Element => {
  return (
    <section className={style.fullWidth}>
      <Flex gap="md" direction="column" fullWidth>
        <div className={affiliateProgramStyle.faqSection}>
          <Flex direction="column" fullWidth>
            <Heading as="h2" size="small">
              Frequently asked questions
            </Heading>
            {faqItems.map((faq, index) => (
              <React.Fragment key={faq.question}>
                <Collapsible className={affiliateProgramStyle.faqItem}>
                  <Collapsible.Trigger
                    className={affiliateProgramStyle.faqTrigger}
                  >
                    <Text weight="medium" color="default">
                      {faq.question}
                    </Text>
                  </Collapsible.Trigger>
                  <Collapsible.Content
                    className={affiliateProgramStyle.faqContent}
                  >
                    <Text color="subtle">{faq.answer}</Text>
                  </Collapsible.Content>
                </Collapsible>
                {index < faqItems.length - 1 && (
                  <div className={affiliateProgramStyle.faqDivider} />
                )}
              </React.Fragment>
            ))}
          </Flex>
        </div>
      </Flex>
    </section>
  )
}

const AffiliateErrorState = (): JSX.Element => {
  return (
    <NewSettingsPageLayout title="Partner Program" gap="xl">
      <EmptyState variant="critical">
        <EmptyState.Icon>
          <WarningIcon size="large" color="critical" />
        </EmptyState.Icon>
        <EmptyState.Heading>
          We&apos;re having trouble loading your affiliate data
        </EmptyState.Heading>
        <EmptyState.Description>
          This could be temporary, you can try refreshing the page. You can also
          view your affiliate dashboard by logging into your{' '}
          <Link href="https://partners.dub.co/" external>
            Dub account
          </Link>{' '}
          if you&apos;d like.
        </EmptyState.Description>
        <EmptyState.Actions>
          <Button
            as="a"
            href="https://partners.dub.co/"
            target="_blank"
            rel="noopener"
            variant="secondary"
            size="medium"
          >
            Go to Dub
          </Button>
          <Button
            variant="primary"
            size="medium"
            onClick={() => window.location.reload()}
          >
            Refresh
          </Button>
        </EmptyState.Actions>
      </EmptyState>
    </NewSettingsPageLayout>
  )
}

const AffiliateProgramContent = (): JSX.Element => {
  // Query for affiliate status
  const {
    data: statusData,
    loading: statusLoading,
    error: statusError,
    refetch: statusRefetch,
  } = useQuery(GetAffiliateStatus, {
    errorPolicy: 'all',
  })

  // Lazy query for embed data - only fetched when status is active
  const [
    fetchEmbedData,
    { data: embedData, loading: embedLoading, error: embedError },
  ] = useLazyQuery(GetAffiliateEmbedData, {
    errorPolicy: 'all',
  })

  const affiliateStatus = statusData?.affiliateData?.status
  const embedToken = embedData?.affiliateData?.embedData?.token
  const embedTokenExpiresAt =
    embedData?.affiliateData?.embedData?.tokenExpiresAt ?? undefined

  // Get dark mode state for Dub embed styling
  const isDarkMode = useIsDarkMode()

  // Map Buffer themes to Dub options configuration
  const dubOptions: DubEmbedOptions = React.useMemo(() => {
    // Get the background color from Popcorn's CSS custom property
    const backgroundColor = getComputedStyle(document.documentElement)
      .getPropertyValue('--color-bg-neutral')
      .trim()

    return {
      theme: isDarkMode ? 'dark' : 'light',
      themeOptions: {
        backgroundColor,
      },
    }
  }, [isDarkMode])

  // Fetch embed data when status becomes active
  useEffect(() => {
    if (affiliateStatus === 'active') {
      void fetchEmbedData()
    }
  }, [affiliateStatus, fetchEmbedData])

  // Show toast notification if there's an error fetching affiliate status
  useEffect(() => {
    if (statusError) {
      toast.error(
        "We couldn't fetch your affiliate status. Please try again later or contact support if the problem persists.",
      )
    }
  }, [statusError])

  // Auto-refresh when user returns to page, but only when not enrolled
  const handleRefresh = useCallback(() => {
    void statusRefetch()
  }, [statusRefetch])

  const shouldRefresh = useCallback(
    () => affiliateStatus === 'notEnrolled',
    [affiliateStatus],
  )

  usePageVisibilityRefresh(handleRefresh, shouldRefresh)

  // Show loading skeleton while fetching status
  if (statusLoading) {
    return (
      <NewSettingsPageLayout title="Partner Program" gap="xl">
        <Skeleton width="100%" height="400px" />
      </NewSettingsPageLayout>
    )
  }

  // Not enrolled - show enrollment state
  // Also show this if there's an error fetching status (with toast notification)
  if (affiliateStatus === 'notEnrolled' || statusError) {
    return (
      <NewSettingsPageLayout title="Partner Program" gap="3xl">
        <Flex gap="3xl" direction="column" fullWidth>
          <PromotionalBanner
            variant="inline"
            className={affiliateProgramStyle.promotionalBannerWrapper}
          >
            <PromotionalBanner.Content>
              <PromotionalBanner.Heading>
                Earn 25% on every referral
                <br />
                for a full year
              </PromotionalBanner.Heading>
              <PromotionalBanner.Description color="subtle">
                Join our Partner Program and earn 25% recurring commission every
                month for a full year for each paying customer you refer.
              </PromotionalBanner.Description>
              <PromotionalBanner.Actions>
                <Button
                  as="a"
                  href="https://partners.dub.co/buffer/apply"
                  target="_blank"
                  rel="noopener"
                  variant="primary"
                >
                  Apply Now
                </Button>
              </PromotionalBanner.Actions>
            </PromotionalBanner.Content>
            <PromotionalBanner.Image
              src="https://buffer-publish.s3.us-east-1.amazonaws.com/images/partner-program.png"
              fallbackColor="#D5DBFF"
              className={affiliateProgramStyle.bannerImage}
            />
          </PromotionalBanner>
          <AffiliateHowItWorks />
          <AffiliateFAQ />
        </Flex>
      </NewSettingsPageLayout>
    )
  }

  // Pending - application under review
  if (affiliateStatus === 'pending') {
    return (
      <NewSettingsPageLayout title="Partner Program" gap="3xl">
        <Notice variant="info" title="Application under review">
          <Text>
            Your affiliate application is currently under review, this may take
            1-2 days. You will receive an email when your application has been
            reviewed.
          </Text>
        </Notice>
        <AffiliateHowItWorks />
        <AffiliateFAQ />
      </NewSettingsPageLayout>
    )
  }

  // Rejected - application not approved
  if (affiliateStatus === 'rejected') {
    return (
      <NewSettingsPageLayout title="Partner Program" gap="3xl">
        <Notice variant="warning" title="Application not approved">
          <Text>
            Unfortunately, your partner application was not approved at this
            time. If you have questions or would like to discuss this further,
            please{' '}
            <Link href="https://support.buffer.com" external>
              contact our support team
            </Link>
            .
          </Text>
        </Notice>
        <AffiliateHowItWorks />
        <AffiliateFAQ />
      </NewSettingsPageLayout>
    )
  }

  // Revoked - show revoked message
  if (affiliateStatus === 'revoked') {
    return (
      <NewSettingsPageLayout title="Partner Program" gap="3xl">
        <Notice variant="warning" title="Access Revoked">
          <Text>
            Your access to the Buffer Affiliate Program has been revoked. Please
            contact support if you believe this is an error.
          </Text>
        </Notice>
        <AffiliateHowItWorks />
        <AffiliateFAQ />
      </NewSettingsPageLayout>
    )
  }

  // Active - show embed or loading
  if (affiliateStatus === 'active') {
    // Show error if embed data query failed (check this first)
    if (embedError) {
      return <AffiliateErrorState />
    }

    // Still loading embed data
    if (embedLoading || !embedToken) {
      return (
        <NewSettingsPageLayout title="Partner Program" gap="xl">
          <Skeleton width="100%" height="600px" />
        </NewSettingsPageLayout>
      )
    }

    // Render the Dub embed
    return (
      <NewSettingsPageLayout title="Partner Program" gap="xl">
        <Text color="subtle">
          The dashboard is powered by Dub. For full access, you can open it
          directly on Dub.
        </Text>
        <section className={style.fullWidth}>
          <div
            className={affiliateProgramStyle.embedContainer}
            data-token-expires-at={embedTokenExpiresAt}
          >
            <DubEmbed
              data="referrals"
              token={embedToken}
              options={dubOptions}
            />
          </div>
        </section>
      </NewSettingsPageLayout>
    )
  }

  // Fallback for unknown status - show error state
  return <AffiliateErrorState />
}

const AffiliateProgram = (): JSX.Element => (
  <AffiliateProgramRoute>
    <AffiliateProgramContent />
  </AffiliateProgramRoute>
)

export default AffiliateProgram
